import { Link, useRouter, useRouterState } from "@tanstack/react-router";
import { Suspense, lazy, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Crown, Filter, User, LogOut, LayoutGrid, Megaphone, UserCircle, Phone, Mail, FileText, Target, Shield, Calculator } from "lucide-react";
import logo from "@/assets/bizaclia-logo.svg";
import { useAnunciosFilters } from "@/contexts/anuncios-filters";
import { useAgenciasFilters } from "@/contexts/agencias-filters";
import { countActiveAgenciasFilters } from "@/components/admin/AgenciasFiltersSheet";
import { supabase } from "@/integrations/supabase/client";
import { useDataScope } from "@/hooks/useDataScope";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { getOnboardingStatus, getOnboardingFlags, setOnboardingFlag } from "@/lib/onboarding.functions";
import { getMyDemandaBadge } from "@/lib/conector.functions";
import { useIsVip } from "@/hooks/use-user-roles";
import type { AnunciosFilters } from "@/components/admin/AnunciosFiltersSheet";

const AuthDialog = lazy(() =>
  import("@/components/site/AuthDialog").then((m) => ({ default: m.AuthDialog })),
);
const LoginSuccessOverlay = lazy(() =>
  import("@/components/site/LoginSuccessOverlay").then((m) => ({ default: m.LoginSuccessOverlay })),
);
const AdminGlobalSearch = lazy(() =>
  import("@/components/admin/AdminGlobalSearch").then((m) => ({ default: m.AdminGlobalSearch })),
);
const AgenciaVistaCombobox = lazy(() =>
  import("@/components/admin/AgenciaVistaCombobox").then((m) => ({ default: m.AgenciaVistaCombobox })),
);

// Conteo local sin importar el módulo pesado de AnunciosFiltersSheet en la home.
// Cuenta cuántos filtros tienen un valor "activo" (no vacío / no por defecto).
function countActiveFilters(f: AnunciosFilters): number {
  let n = 0;
  for (const [, v] of Object.entries(f ?? {})) {
    if (v == null) continue;
    if (Array.isArray(v)) {
      if (v.length > 0) n += 1;
    } else if (typeof v === "string") {
      if (v.trim() !== "") n += 1;
    } else if (typeof v === "boolean") {
      if (v) n += 1;
    } else if (typeof v === "number") {
      if (!Number.isNaN(v)) n += 1;
    } else if (typeof v === "object") {
      if (Object.keys(v).length > 0) n += 1;
    }
  }
  return n;
}




export function Header() {
  const router = useRouter();
  const isAdminRoute = useRouterState({
    select: (state) => state.location.pathname.startsWith("/admin"),
  });
  const isHomeRoute = useRouterState({
    select: (state) => state.location.pathname === "/",
  });
  const isAnunciosRoute = useRouterState({
    select: (state) =>
      state.location.pathname === "/admin/anuncios" ||
      state.location.pathname.startsWith("/admin/anuncios/"),
  });
  const isAgenciasRoute = useRouterState({
    select: (state) =>
      state.location.pathname === "/admin/agencias" ||
      state.location.pathname.startsWith("/admin/agencias/"),
  });
  const { open: filtersOpen, setOpen: setFiltersOpen, filters } = useAnunciosFilters();
  const activeFilters = countActiveFilters(filters);
  const {
    open: agFiltersOpen,
    setOpen: setAgFiltersOpen,
    filters: agFilters,
  } = useAgenciasFilters();
  const activeAgFilters = countActiveAgenciasFilters(agFilters);
  const { isStaff, puedeVerAgencia, memberships } = useDataScope();
  const canAccessAdmin = isStaff || (memberships?.length ?? 0) > 0;

  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [authOpen, setAuthOpen] = useState(false);
  const [forceOnboarding, setForceOnboarding] = useState(false);
  const [showLoginSuccess, setShowLoginSuccess] = useState(false);

  useEffect(() => {
    const handleLoginComplete = () => setShowLoginSuccess(true);

    const checkOnboarding = async () => {
      try {
        const { completed } = await getOnboardingStatus();
        if (!completed) {
          setForceOnboarding(true);
          setAuthOpen(true);
        } else {
          setForceOnboarding(false);
        }
      } catch {
        // silencioso: si falla, no bloqueamos la UI
      }
    };

    supabase.auth.getSession().then(({ data }) => {
      setUserEmail(data.session?.user.email ?? null);
      if (data.session?.user) {
        checkOnboarding();
      }
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_evt, session) => {
      setUserEmail(session?.user.email ?? null);
      if (session?.user) {
        checkOnboarding();
      } else {
        setForceOnboarding(false);
      }
    });
    window.addEventListener("bizaclia:login-complete", handleLoginComplete);
    return () => {
      window.removeEventListener("bizaclia:login-complete", handleLoginComplete);
      sub.subscription.unsubscribe();
    };
  }, []);

  const displayName = userEmail
    ? userEmail
        .split("@")[0]
        .split(/[._-]+/)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ")
    : null;
  const { isVip } = useIsVip();

  const fnDemandaBadge = useServerFn(getMyDemandaBadge);
  const demandaBadgeQ = useQuery({
    queryKey: ["header-demanda-badge", userEmail ?? "anon"],
    queryFn: () =>
      fnDemandaBadge() as Promise<{ id: string; estado: string; match_count: number } | null>,
    enabled: !!userEmail,
    staleTime: 60_000,
  });
  const demandaBadge = demandaBadgeQ.data;

  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [showDemandaHint, setShowDemandaHint] = useState(false);
  const [showAnunciosHint, setShowAnunciosHint] = useState(false);
  const badgeRef = useRef<HTMLAnchorElement>(null);
  const [demandaHintPos, setDemandaHintPos] = useState<{
    left: number;
    top: number;
    arrowLeft: number;
  } | null>(null);

  const queryClient = useQueryClient();
  const fnGetFlags = useServerFn(getOnboardingFlags);
  const fnSetFlag = useServerFn(setOnboardingFlag);
  const flagsQ = useQuery({
    queryKey: ["onboarding-flags", userEmail ?? "anon"],
    queryFn: () => fnGetFlags(),
    enabled: !!userEmail,
    staleTime: 5 * 60_000,
  });
  const flags = flagsQ.data ?? {};

  // Snapshot del flag del primer cartel en el momento en que cargan los flags.
  // Si ya estaba marcado antes de esta sesión, permitimos que el segundo cartel
  // aparezca por sí solo (p. ej. tras recargar). Si se marca durante la sesión
  // actual (al cerrar el primero), el encadenado en dismissDemandaHint se encarga.
  const firstHintSeenAtMountRef = useRef<boolean | null>(null);
  useEffect(() => {
    if (firstHintSeenAtMountRef.current !== null) return;
    if (!flagsQ.data) return;
    firstHintSeenAtMountRef.current = !!flagsQ.data.demanda_badge_hint_seen;
  }, [flagsQ.data]);

  useEffect(() => {
    if (!userEmail) return;
    if (!demandaBadge) return;
    if (demandaBadge.estado === "incompleta") return;
    if (!pathname.startsWith("/conector/")) return;
    if (flags.demanda_badge_hint_seen) return;
    const t = window.setTimeout(() => setShowDemandaHint(true), 600);
    return () => window.clearTimeout(t);
  }, [userEmail, demandaBadge, pathname, flags.demanda_badge_hint_seen]);

  useEffect(() => {
    if (!userEmail) return;
    if (!pathname.startsWith("/conector/")) return;
    if (!demandaBadge) return;
    if (demandaBadge.estado === "incompleta") return;
    if (!flags.demanda_badge_hint_seen) return;
    if (flags.anuncios_like_hint_seen) return;
    if (showDemandaHint) return;
    // Solo auto-mostrar el segundo si el primero ya estaba visto ANTES de esta
    // sesión. Si se marcó durante esta sesión, el encadenado desde
    // dismissDemandaHint es quien debe abrirlo.
    if (firstHintSeenAtMountRef.current !== true) return;
    const t = window.setTimeout(() => setShowAnunciosHint(true), 600);
    return () => window.clearTimeout(t);
  }, [userEmail, pathname, demandaBadge, flags.demanda_badge_hint_seen, flags.anuncios_like_hint_seen, showDemandaHint]);


  useEffect(() => {
    if (!showDemandaHint) {
      setDemandaHintPos(null);
      return;
    }
    if (!badgeRef.current) return;

    const updatePos = () => {
      if (!badgeRef.current) return;
      const rect = badgeRef.current.getBoundingClientRect();
      const popupWidth = 288; // w-72
      const minLeft = 5;
      const gap = 12; // mt-3
      const left = Math.max(minLeft, rect.right - popupWidth);
      const top = rect.bottom + gap;
      const arrowLeft = rect.left + rect.width / 2 - left - 6;
      setDemandaHintPos({ left, top, arrowLeft });
    };

    updatePos();
    window.addEventListener("resize", updatePos);
    return () => window.removeEventListener("resize", updatePos);
  }, [showDemandaHint]);

  const persistFlag = async (key: "demanda_badge_hint_seen" | "anuncios_like_hint_seen") => {
    try {
      await fnSetFlag({ data: { key, value: true } });
      queryClient.setQueryData<typeof flags>(
        ["onboarding-flags", userEmail ?? "anon"],
        (prev) => ({ ...(prev ?? {}), [key]: true }),
      );
    } catch {
      // silencioso
    }
  };

  const dismissDemandaHint = () => {
    setShowDemandaHint(false);
    void persistFlag("demanda_badge_hint_seen");
    if (!flags.anuncios_like_hint_seen && demandaBadge?.estado !== "incompleta") {
      window.setTimeout(() => setShowAnunciosHint(true), 250);
    }
  };
  const dismissAnunciosHint = () => {
    setShowAnunciosHint(false);
    void persistFlag("anuncios_like_hint_seen");
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    router.navigate({ to: "/" });
  };



  return (
    <header className="sticky top-0 z-40 w-full bg-background/85 backdrop-blur-md border-b border-border/60">
      <div className="container mx-auto max-w-7xl flex items-center justify-between h-16 px-4 sm:px-6">
        <Link to="/" className="flex items-center group" aria-label="Bizaclia.com">
          <img src={logo} alt="Bizaclia.com" width={140} height={28} className="h-7 w-auto" />
        </Link>

        {isHomeRoute && (
          <nav className="hidden md:flex items-center gap-7 text-sm font-medium text-muted-foreground">
            <Link
              to="/estimador"
              activeProps={{ className: "text-foreground" }}
              className="hover:text-foreground transition"
            >
              Valorador
            </Link>
            <Link
              to="/estimador-vivienda"
              activeProps={{ className: "text-foreground" }}
              className="hover:text-foreground transition"
            >
              Valorar vivienda
            </Link>
            <a href="#como-funciona" className="hover:text-foreground transition">Cómo funciona</a>
            <a href="#ventajas" className="hover:text-foreground transition">Ventajas</a>
            <a href="#categorias" className="hover:text-foreground transition">Categorías</a>
            <a href="#testimonios" className="hover:text-foreground transition">Opiniones</a>
          </nav>
        )}

        {isAdminRoute && (
          <div className="hidden md:flex flex-1 items-center justify-center px-6 gap-3">
            {(isAnunciosRoute || isAgenciasRoute) && (
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  isAgenciasRoute
                    ? setAgFiltersOpen(!agFiltersOpen)
                    : setFiltersOpen(!filtersOpen)
                }
                className="gap-1.5"
              >
                <Filter className="h-4 w-4" />
                Filtros
                {(isAgenciasRoute ? activeAgFilters : activeFilters) > 0 && (
                  <Badge variant="secondary" className="ml-1 h-5 min-w-5 px-1.5 text-[10px]">
                    {isAgenciasRoute ? activeAgFilters : activeFilters}
                  </Badge>
                )}
              </Button>
            )}
            {puedeVerAgencia && (
              <Suspense fallback={null}>
                <AgenciaVistaCombobox />
              </Suspense>
            )}

            <Suspense fallback={null}>
              <AdminGlobalSearch />
            </Suspense>
          </div>
        )}

        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="inline-flex md:hidden h-10 w-10 rounded-full border border-border hover:bg-primary-soft hover:text-foreground"
            asChild
            aria-label="Valorador de valor de negocio"
          >
            <Link to="/estimador">
              <Calculator className="h-5 w-5" />
            </Link>
          </Button>
          <Button
            variant="ghost"
            className="hidden sm:inline-flex text-sm font-semibold hover:bg-primary-soft hover:text-foreground"
            asChild
          >
            <Link to="/mybizaclia" search={{ tab: "anuncios", wizard: true }}>
              Publicar anuncio
            </Link>
          </Button>
          {displayName && demandaBadge ? (
            (() => {
              const activo = ["iniciada", "en_visitas", "negociacion", "arras"].includes(
                demandaBadge.estado,
              );
              return (
                <div className="relative">
                  <Link
                    ref={badgeRef}
                    to="/conector/{-$id}"
                    params={{ id: demandaBadge.id }}
                    search={{} as never}
                    aria-label={`Tienes ${demandaBadge.match_count} coincidencias`}
                    title={`${demandaBadge.match_count} coincidencias`}
                    className={`relative inline-flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold text-white shadow-sm transition hover:opacity-90 ${
                      activo ? "bg-emerald-500 hover:bg-emerald-600" : "bg-muted-foreground/60 hover:bg-muted-foreground/70"
                    } ${showDemandaHint ? "ring-4 ring-emerald-400/40 animate-pulse" : ""}`}
                  >
                    {demandaBadge.match_count}
                  </Link>
                  {showDemandaHint && demandaHintPos ? (
                    <>
                      <div
                        className="fixed inset-0 z-40 bg-black/40 animate-fade-in"
                        aria-hidden="true"
                      />
                      <div
                        role="dialog"
                        aria-label="Tus coincidencias"
                        className="fixed z-50 w-72 animate-scale-in origin-top-right"
                        style={{ left: demandaHintPos.left, top: demandaHintPos.top }}
                      >
                        <div
                          className="absolute -top-2 h-3 w-3 rotate-45 bg-white border-l border-t border-emerald-200"
                          style={{ left: demandaHintPos.arrowLeft }}
                          aria-hidden="true"
                        />
                        <div className="rounded-xl bg-white p-4 text-left shadow-2xl ring-1 ring-emerald-200">
                          <p className="text-sm font-semibold text-foreground">
                            Tus coincidencias
                          </p>
                          <p className="mt-1 text-xs text-muted-foreground">
                            Desde aquí accedes a tu demanda y a los anuncios que encajan con ella.
                          </p>
                          <div className="mt-3 flex justify-end">
                            <Button
                              size="sm"
                              className="bg-emerald-500 text-white hover:bg-emerald-600"
                              onClick={dismissDemandaHint}
                            >
                              Entendido
                            </Button>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : null}
                </div>
              );
            })()
          ) : null}
          {showAnunciosHint
            ? createPortal(
                <>
                  <div
                    className="fixed inset-0 z-40 bg-black/40 animate-fade-in"
                    aria-hidden="true"
                  />
                  <div
                    role="dialog"
                    aria-label="Marca lo que te interesa"
                    className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
                  >
                    <div className="pointer-events-auto w-[92%] max-w-sm animate-scale-in rounded-xl bg-white p-5 text-left shadow-2xl ring-1 ring-emerald-200">
                      <p className="text-sm font-semibold text-foreground">
                        Marca lo que te interesa
                      </p>
                      <p className="mt-2 text-sm text-muted-foreground">
                        Dale me gusta a los anuncios que encajan y descarta el resto. Así el conector aprende y te muestra mejores resultados.
                      </p>
                      <div className="mt-4 flex justify-end">
                        <Button
                          size="sm"
                          className="bg-emerald-500 text-white hover:bg-emerald-600"
                          onClick={dismissAnunciosHint}
                        >
                          Entendido
                        </Button>
                      </div>
                    </div>
                  </div>
                </>,
                document.body,
              )
            : null}
          {displayName ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="rounded-full px-4 h-10 gap-2 text-sm font-semibold hover:bg-primary-soft hover:text-foreground border border-border"
                >
                  <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-ink text-ink-foreground">
                    <User className="h-4 w-4" />
                  </span>
                  <span className="truncate max-w-[140px]">{displayName}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                {canAccessAdmin && (
                  <DropdownMenuItem asChild>
                    <Link to="/admin" className="cursor-pointer font-semibold">
                      <Shield className="mr-2 h-4 w-4" /> Panel Admin
                    </Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem asChild>
                  <Link to="/mybizaclia" className="cursor-pointer">
                    <LayoutGrid className="mr-2 h-4 w-4" /> My Bizaclia
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link
                    to="/mybizaclia"
                    search={{ tab: "anuncios" }}
                    className="cursor-pointer"
                    onClick={() => window.dispatchEvent(new CustomEvent("bizaclia:reset-mis-anuncios"))}
                  >
                    <Megaphone className="mr-2 h-4 w-4" /> Mis Anuncios
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/mybizaclia" search={{ tab: "solicitudes" }} className="cursor-pointer">
                    <FileText className="mr-2 h-4 w-4" /> Solicitudes
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/mybizaclia" search={{ tab: "demandas" }} className="cursor-pointer">
                    <Target className="mr-2 h-4 w-4" /> Demandas
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/mybizaclia" search={{ tab: "cuenta" }} className="cursor-pointer">
                    <UserCircle className="mr-2 h-4 w-4" /> Mi cuenta
                  </Link>
                </DropdownMenuItem>
                {isVip && (
                  <DropdownMenuItem asChild>
                    <Link to="/vip" className="cursor-pointer text-amber-700 focus:text-amber-800 focus:bg-amber-50">
                      <Crown className="mr-2 h-4 w-4" /> Bizaclia VIP
                    </Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs uppercase tracking-wide text-muted-foreground font-medium">
                  Atención al cliente
                </DropdownMenuLabel>
                <a
                  href="tel:900433065"
                  className="flex items-center px-2 py-1.5 text-sm hover:bg-accent rounded-sm mx-1"
                >
                  <Phone className="mr-2 h-4 w-4" /> 900 433 065
                </a>
                <a
                  href="mailto:info@bizaclia.com"
                  className="flex items-center px-2 py-1.5 text-sm hover:bg-accent rounded-sm mx-1"
                >
                  <Mail className="mr-2 h-4 w-4" /> info@bizaclia.com
                </a>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer">
                  <LogOut className="mr-2 h-4 w-4" /> Cerrar sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button
              onClick={() => setAuthOpen(true)}
              className="rounded-full px-5 h-10 bg-ink text-ink-foreground hover:bg-ink/90 text-sm font-semibold"
            >
              Acceder
            </Button>
          )}
        </div>
      </div>
      {(authOpen || forceOnboarding) && (
        <Suspense fallback={null}>
          <AuthDialog
            open={authOpen}
            onOpenChange={(next) => {
              setAuthOpen(next);
              if (!next) setForceOnboarding(false);
            }}
            initialStep={forceOnboarding ? "onboarding-usage" : undefined}
          />
        </Suspense>
      )}
      {showLoginSuccess && (
        <Suspense fallback={null}>
          <LoginSuccessOverlay
            show={showLoginSuccess}
            onDone={() => setShowLoginSuccess(false)}
          />
        </Suspense>
      )}
    </header>
  );
}
